import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import Joi from 'joi';
import { useApolloClient, gql } from '@apollo/client';
import CloseIcon from '../../images/CloseIcon';
import { validate as validatePhone } from '../../phone';
import EmptyButton from '../EmptyButton';
import Text from '../Text';
import Container from '../Container';
import Dialog from '../Dialog/index';
import TextArea from '../TextArea';
import FancyInput from '../FancyInput';
import Image from '../Image';
import Validator from '../Validator';
import StepperForm from '../StepperForm';
import successMessageIcon from '../../images/successMessageIcon.svg';
import User from './user';
import Mail from './mail';
import MapPin from './mapPin';
import Phone from './phone';

const createContactUsMutation = gql`
  mutation createContactUs($input: CreateContactUsInput!) {
    createContactUs(input: $input) {
      contactUs {
        message
        companyName
        email
        address
        phone
        firstName
      }
    }
  }
`;
const emailSchema = Joi.string().email({ tlds: false }).messages({
  'string.empty': 'You forgot your email address',
  'string.email': 'Invalid email address',
});
const schema = Joi.object({
  message: Joi.string().required().messages({
    'string.empty': 'Message is required',
  }),
  companyName: Joi.string().required().messages({
    'string.empty': 'Company Name is required',
  }),
  email: emailSchema,
  address: Joi.string().required().messages({
    'string.empty': 'Address is required',
  }),
  phone: Joi.string()
    .custom((value, { error }) =>
      validatePhone(value) ? value : error('invalid')
    )
    .messages({
      invalid: 'Invalid phone number',
      'string.empty': 'You forgot your phone number',
    }),
  firstName: Joi.string().required().messages({
    'string.empty': 'First Name is required',
  }),
});

const ContactUs = ({ show, mode, onClose, onSubmitSuccess, ...props }) => {
  const client = useApolloClient();
  const [showSuccess, setShowSuccess] = useState(false);
  const {
    register,
    formState: { errors, isValid, isSubmitting },
    handleSubmit,
    reset,
  } = useForm({
    shouldUnregister: true,
    defaultValues: {
      message: '',
      companyName: '',
      email: '',
      address: '',
      phone: '',
      firstName: '',
    },
    resolver: joiResolver(schema),
  });

  const onSubmit = async values => {
    const { message, companyName, email, address, phone, firstName } = values;
    await client.mutate({
      mutation: createContactUsMutation,
      variables: {
        input: {
          message,
          companyName,
          email,
          address,
          phone,
          firstName,
        },
      },
    });
    onSubmitSuccess();
    setShowSuccess(true);
    reset();
  };

  const stepContents = [
    {
      id: 1,
      content: (
        <Container>
          <Container marginBottom="8px">
            <Text
              lineHeight="1"
              color="text600"
              fontSize="16px"
              fontWeight="600"
            >
              Message <Text color="#F16159"> *</Text>
            </Text>
          </Container>
          <TextArea
            fontFamily="Circular"
            {...register('message')}
            placeholder="Please give us more information so we can provide the best support."
            maxLength={1000}
            minWidth="100%"
            maxWidth="100%"
            backgroundColor="#f8f8f8"
            boxShadow="none"
            border="none"
            required
          />
          <Container display="flex" justifyContent="flex-end" marginTop="3px">
            <Validator fontSize="14px">{errors.message?.message}</Validator>
          </Container>
        </Container>
      ),
    },
    {
      id: 2,
      content: (
        <Container>
          <Container marginBottom="15px">
            <FancyInput
              label="Company Name"
              {...register('companyName')}
              fontSize="14px"
              placeholder="XXX Company"
              showClear={false}
              required
              border="none"
              backgroundColor="#F8F8F8"
              borderRadius="8px"
              icon={
                <Container width="35px" display="flex" justifyContent="center">
                  <User />
                </Container>
              }
            />
            <Container display="flex" justifyContent="flex-end" marginTop="3px">
              <Validator fontSize="14px">
                {errors.companyName?.message}
              </Validator>
            </Container>
          </Container>
          <Container marginBottom="15px">
            <FancyInput
              label="Email"
              {...register('email')}
              fontSize="14px"
              placeholder="user@email.com.au"
              showClear={false}
              required
              border="none"
              backgroundColor="#F8F8F8"
              borderRadius="8px"
              icon={
                <Container width="35px" display="flex" justifyContent="center">
                  <Mail />
                </Container>
              }
            />
            <Container display="flex" justifyContent="flex-end" marginTop="3px">
              <Validator fontSize="14px">{errors.email?.message}</Validator>
            </Container>
          </Container>
          <Container marginBottom="15px">
            <FancyInput
              label="Address"
              {...register('address')}
              fontSize="14px"
              placeholder="138 Robina Dr, Robina"
              showClear={false}
              required
              border="none"
              backgroundColor="#F8F8F8"
              borderRadius="8px"
              icon={
                <Container width="35px" display="flex" justifyContent="center">
                  <MapPin />
                </Container>
              }
            />
            <Container display="flex" justifyContent="flex-end" marginTop="3px">
              <Validator fontSize="14px">{errors.address?.message}</Validator>
            </Container>
          </Container>
          <Container marginBottom="15px">
            <FancyInput
              label="Phone"
              {...register('phone')}
              fontSize="14px"
              placeholder="04 xxxx xxxx"
              showClear={false}
              required
              border="none"
              backgroundColor="#F8F8F8"
              borderRadius="8px"
              icon={
                <Container width="35px" display="flex" justifyContent="center">
                  <Phone />
                </Container>
              }
            />
            <Container display="flex" justifyContent="flex-end" marginTop="3px">
              <Validator fontSize="14px">{errors.phone?.message}</Validator>
            </Container>
          </Container>
          <Container marginBottom="15px">
            <FancyInput
              label="First Name"
              {...register('firstName')}
              fontSize="14px"
              placeholder="First Name"
              showClear={false}
              required
              border="none"
              backgroundColor="#F8F8F8"
              borderRadius="8px"
              icon={
                <Container width="35px" display="flex" justifyContent="center">
                  <User />
                </Container>
              }
            />
            <Container display="flex" justifyContent="flex-end" marginTop="3px">
              <Validator fontSize="14px">{errors.firstName?.message}</Validator>
            </Container>
          </Container>
        </Container>
      ),
    },
  ];

  const stepsTitle = ['Contact Us', 'Confirm your details'];
  return (
    <>
      <Dialog
        zIndex="99999999"
        show={show}
        maxWidth="408px"
        border="none"
        mode={mode}
        {...props}
      >
        <StepperForm
          totalSteps={stepContents.length}
          stepContents={stepContents}
          padding="30px 30px 15px 30px"
          onSubmit={handleSubmit(onSubmit)}
          loading={isSubmitting}
          error={errors}
          disabled={!isValid}
          onClose={onClose}
          mode={mode}
          stepsTitle={stepsTitle}
        />
      </Dialog>
      <Dialog
        show={showSuccess}
        maxWidth="408px"
        clickOutside
        onClose={() => setShowSuccess(false)}
      >
        <Container
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          padding="30px"
          flexDirection="column"
        >
          <Container
            width="100%"
            marginBottom="20px"
            display="flex"
            justifyContent="end"
          >
            <EmptyButton
              onClick={() => setShowSuccess(false)}
              display="flex"
              alignItems="center"
            >
              <CloseIcon width="20px" fill="black" />
            </EmptyButton>
          </Container>
          <Image margin="15px 0" src={successMessageIcon} />
          <Text fontSize="26px" fontWeight="700">
            Message Sent!
          </Text>
          <Container margin="15px 0">
            <Text
              display="flex"
              textAlign="center"
              fontSize="16px"
              fontWeight="450"
            >
              We&apos;ve received your message. We&apos;ll get back to you as
              soon as possible.
            </Text>
          </Container>
          <EmptyButton
            color="primary"
            fontSize="16px"
            fontWeight="600"
            backgroundColor="#F8F8F8"
            borderRadius="30px"
            padding="10px 20px"
            marginTop="10px"
            _hover={{
              backgroundColor: '#ececec',
            }}
            margin="15px 0"
            onClick={() => setShowSuccess(false)}
          >
            Done
          </EmptyButton>
        </Container>
      </Dialog>
    </>
  );
};

ContactUs.propTypes = {
  show: PropTypes.bool,
  mode: PropTypes.string,
  onClose: PropTypes.func,
  onSubmitSuccess: PropTypes.func,
};

ContactUs.defaultProps = {
  show: false,
  mode: undefined,
  onClose: () => {},
  onSubmitSuccess: () => {},
};

export default ContactUs;
